import React, { useEffect, useRef } from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  useTheme,
  Chip,
} from "@mui/material";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  RocketLaunch,
  Code,
  DesignServices,
  CloudQueue,
  Timeline,
  Star,
  FlightTakeoff,
  CheckCircle,
  Security,
  Speed,
  Hub,
} from "@mui/icons-material";
import ViraItems from "./ViraItems";

gsap.registerPlugin(ScrollTrigger);

export default function ViraSct() {
  const theme = useTheme();
  const timelineRef = useRef(null);
  const itemsRef = useRef([]);

  const milestones = [
    {
      year: "۱۵۰+",
      title: "پروژه فعال و موفق",
      subtitle: "توسعه در مقیاس سازمانی",
      description:
        "طراحی، استقرار و پشتیبانی از سامانه‌های نرم‌افزاری توزیع‌شده برای ارگان‌های دولتی و بخش خصوصی کلان.",
      icon: <CheckCircle />,
      color: "#5d49f5",
      services: ["پلتفرم‌های چندمستأجری", "اتوماسیون فرآیندها"],
    },
    {
      year: "۹۹.۹۸٪",
      title: "پایداری زیرساخت (Uptime)",
      subtitle: "معماری ابری با تاب‌آوری بالا",
      description:
        "پیاده‌سازی بسترهای میکروسرویس بر روی کلاسترهای توزیع‌شده با تضمین پایداری و بدون قطعی (Fault Tolerance).",
      icon: <Hub />,
      color: "#274c83",
      services: ["Cloud Architecture", "High Availability"],
    },
    {
      year: "Zero",
      title: "نفوذپذیری و امنیت داده",
      subtitle: "حفاظت سخت‌گیرانه از اطلاعات",
      description:
        "استفاده از پروتکل‌های رمزنگاری پیشرفته و لایه‌های امنیتی چندگانه جهت صیانت از دیتای حساس سازمان‌ها.",
      icon: <Security />,
      color: "#1c8e8c",
      services: ["تست نفوذ دوره ای", "پروتکل‌های احراز هویت"],
    },
    {
      year: "۴X",
      title: "بهینه‌سازی پرفورمنس",
      subtitle: "سرعت پردازش در حجم دیتای بالا",
      description:
        "ارتقای چشم‌گیر سرعت لود و پردازش درخواست‌ها با بهینه‌سازی کوئری‌های کلان‌داده و لایه‌های کَشینگ.",
      icon: <Speed />,
      color: "#12c895",
      services: ["Big Data Processing", "پایگاه داده‌های توزیع‌شده"],
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".timeline-line", {
        scrollTrigger: {
          trigger: timelineRef.current,
          start: "top 70%",
          end: "bottom 80%",
          scrub: 1,
        },
        height: 0,
        duration: 2,
        ease: "power2.inOut",
      });

      itemsRef.current.forEach((item, index) => {
        if (item) {
          gsap.from(item, {
            scrollTrigger: {
              trigger: item,
              start: "top 85%",
              end: "bottom 70%",
              toggleActions: "play none none reverse",
            },
            x: index % 2 === 0 ? -80 : 80,
            opacity: 0,
            duration: 0.8,
            ease: "back.out(0.6)",
            delay: index * 0.1,
          });
        }
      });
    });

    return () => ctx.revert();
  }, []);

  const items = milestones.map((item, index) => (
    <ViraItems key={index} index={index} item={item} itemsRef={itemsRef} />
  ));

  return (
    <Box
      ref={timelineRef}
      sx={{
        py: { xs: 8, md: 15 },
        position: "relative",
        overflow: "hidden",
        "& *": {
          direction: "rtl",
        },
      }}
    >
      {/* decoration effect*/}
      <Box
        sx={{
          position: "absolute",
          top: "20%",
          left: "50%",
          width: "80%",
          height: "80%",
          transform: "translate(-50%, -50%)",
          background:
            "radial-gradient(circle, rgba(25,118,210,0.05), transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Header*/}
      <Container maxWidth="lg" sx={{ mb: 8, textAlign: "center" }}>
        <Typography
          sx={{
            fontSize: { xs: 30, lg: 44 },
            fontWeight: 900,
            color: "secondary.main",
            mb: 2,
            textTransform: "uppercase",
            fontFamily: "Vazirmatn, sans-serif !important",
          }}
        >
          zenith of imagination
        </Typography>
        <Typography
          sx={{
            fontSize: 18,
            color: "rgba(255,255,255,0.6)",
            maxWidth: 600,
            mx: "auto",
          }}
        >
          تا آنجا که تخیل اجازه میدهد
        </Typography>
      </Container>

      {/* Container */}
      <Container maxWidth="xl" sx={{ position: "relative" }}>
        {/* Line */}
        <Box
          className="timeline-line"
          sx={{
            position: "absolute",
            left: { xs: "20px", md: "50%" },
            transform: { xs: "translateX(0)", md: "translateX(-50%)" },
            width: "2px",
            height: "100%",
            backgroundColor: "gray",
            borderRadius: 2,
            zIndex: 0,
          }}
        />

        {items}
      </Container>

      {/* animations pulse */}
      <style>
        {`
          @keyframes pulse {
            0% {
              transform: scale(1);
              opacity: 0.6;
            }
            100% {
              transform: scale(1.5);
              opacity: 0;
            }
          }
        `}
      </style>
    </Box>
  );
}
